import { createContext, useContext } from "react";
import type { Project } from "@gb/contracts";

/**
 * The project the current URL is about.
 *
 * Its own file rather than living beside `ProjectLayout`, because the chrome
 * consumes it: `ProjectLayout` renders `AppShell`, which renders `Topbar`,
 * which needs the project — importing the hook from the layout would close a
 * cycle.
 *
 * The value is non-nullable by construction. `ProjectLayout` resolves the id
 * and renders its own pending / not-found / error states, so nothing below it
 * ever sees a missing project — which is what let `useProjectName()` drop its
 * "No project" fallback and every screen drop a null branch.
 */
export type ProjectContextValue = {
  project: Project;
  projectId: string;
};

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export function useProjectContext(): ProjectContextValue {
  const value = useContext(ProjectContext);
  if (!value) {
    throw new Error(
      "useProjectContext() outside a project route. Screens that need a project must be rendered under ProjectLayout.",
    );
  }
  return value;
}
